import { DeleteEvent, GetEvent, PatchEvent, SubmitEvent } from '~/machines/base-crud/events';
import { ProviderPrivilege } from '~/types';
import {
  apiAxiosV1,
  deleteData,
  getData,
  patchData,
  postData,
  setPathParams,
} from '~/utils/service';
import { OptionType } from '../../types';
import { specialtiesToOptions } from '../aoi/utils';
import { OnCreateMultipleEvent, OnDeleteMultipleEvent, PrivilegeMachineEvents } from './events';
import {
  MedicalPrivilegesPaginatedResponse,
  PrivilegeMachineContext,
  PrivilegesContext,
  PrivilegesFormValues,
} from './types';
import {
  privilegePatchFormToApi,
  providerPrivilegeApiToForm,
  providerPrivilegeFormToApi,
  providerPrivilegesApiToForm,
  providerPrivilegesBulkApiToForm,
  providerPrivilegesFormToApi,
} from './utils';

const ROUTE_NAME = 'privileges';
const BULK_ROUTE_NAME = 'privilegesBulkActions';

interface PrivilegesInput<T> {
  input: {
    context: PrivilegesContext;
    event: T;
  };
}

export const PRIVILEGES_ROUTES = {
  get: '/provider-privileges/{providerId}',
  post: '/provider-privileges/{providerId}',
  patch: '/provider-privileges/{id}',
  delete: '/provider-privileges/{id}',
};

export const BULK_PRIVILEGES_ROUTES = {
  post: '/provider-privileges/bulk/{providerId}',
  delete: '/provider-privileges/bulk/{providerId}',
};

export const AVAILABLE_PRIVILEGES_ROUTES = {
  get: '/provider-privileges/medical-privileges/{providerId}',
};

export const EXPORT_PRIVILEGES_ROUTES = {
  get: '/provider-privileges/export/{providerId}',
};

export const API_CALLS = {
  get: async ({ input }: PrivilegesInput<GetEvent>): Promise<ProviderPrivilege[]> => {
    return await getData(ROUTE_NAME, {
      sort: input.context.sort,
      pathParams: { providerId: input.context.providerId.toString() },
      transformResponse: providerPrivilegesApiToForm,
    });
  },
  create: async ({
    input,
  }: PrivilegesInput<SubmitEvent<PrivilegesFormValues>>): Promise<ProviderPrivilege> => {
    return await postData(ROUTE_NAME, {
      data: input.event.values,
      pathParams: { providerId: input.context.providerId.toString() },
      transformRequest: providerPrivilegeFormToApi,
      transformResponse: providerPrivilegeApiToForm,
    });
  },
  delete: async ({ input }: PrivilegesInput<DeleteEvent>) => {
    return await deleteData(ROUTE_NAME, {
      pathParams: { id: input.event.payload.id.toString() },
      params: { providerId: input.context.providerId },
    });
  },
  patch: async ({
    input,
  }: PrivilegesInput<PatchEvent<PrivilegesFormValues>>): Promise<ProviderPrivilege> => {
    return await patchData(ROUTE_NAME, {
      pathParams: { id: input.event.values.id?.toString() || '' },
      params: { providerId: input.context.providerId },
      data: input.event.values,
      transformRequest: privilegePatchFormToApi,
      transformResponse: providerPrivilegeApiToForm,
    });
  },
};

export const BULK_API_CALLS = {
  create: async ({
    input,
  }: {
    input: {
      context: PrivilegeMachineContext;
      event: PrivilegeMachineEvents;
    };
  }): Promise<{
    success_count: number;
    failed_count: number;
    errors: string[];
    created_privileges: ProviderPrivilege[];
  }> => {
    const event = input.event as OnCreateMultipleEvent;
    const privileges = event.payload.privileges;
    return await postData(BULK_ROUTE_NAME, {
      pathParams: { providerId: input.context.providerId.toString() },
      data: privileges,
      transformRequest: providerPrivilegesFormToApi,
      transformResponse: providerPrivilegesBulkApiToForm,
    });
  },
  delete: async ({
    input,
  }: {
    input: {
      context: PrivilegeMachineContext;
      event: PrivilegeMachineEvents;
    };
  }): Promise<{
    success_count: number;
    failed_count: number;
    errors: string[];
    deleted_privilege_ids: number[];
  }> => {
    return await deleteData(BULK_ROUTE_NAME, {
      pathParams: { providerId: input.context.providerId.toString() },
      data: {
        privilegeIds: (input.event as OnDeleteMultipleEvent).payload.ids,
      },
    });
  },
};

export const downloadPrivilegesCsv = async (props: {
  input: { context: PrivilegeMachineContext };
}) => {
  const response = await apiAxiosV1.get<Blob>(
    setPathParams(EXPORT_PRIVILEGES_ROUTES.get, {
      providerId: props.input.context.providerId.toString(),
    }),
    {
      responseType: 'blob',
    }
  );
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  const today = new Date().toISOString().split('T')[0];
  const filename = `provider_privileges_${props.input.context.providerId}_${today}.csv`;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
  return response.data;
};

export const getAvailablePrivileges = async (props: {
  input: { context: PrivilegeMachineContext };
}) => {
  return await getData<MedicalPrivilegesPaginatedResponse>('availablePrivileges', {
    pathParams: { providerId: props.input.context.providerId.toString() },
  });
};

export const loadSpecialtyOptions = async (providerCategoryId: number, searchTerm?: string) => {
  return await getData<OptionType[]>('aoisSpec', {
    pathParams: { providerCategoryId: providerCategoryId.toString() },
    params: { specialty_name: searchTerm },
    transformResponse: specialtiesToOptions,
  });
};
